import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import { cn } from "@v2/lib/utils";
import { supabaseV2 } from "../../lib/supabase";
import { fixEaText } from "../../lib/ea-name";
import { ourClubIdOf } from "../../lib/ea-game";
import { currentIntlLocale } from "../../lib/i18n";
import type { EaMatch } from "../../features/ea-world/types";
import { settingsSections, type View } from "./nav-config";

export function CommandPalette({
  open,
  onOpenChange,
  teamId,
  currentView,
  onChangeView,
  onOpenPlayer,
  teams,
  activeTeamId,
  onSwitchTeam,
  showRequests,
  showAdmin,
  canManageMembers,
  eaClubId,
  onOpenEaMatch,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: string;
  currentView: View;
  onChangeView: (view: View) => void;
  onOpenPlayer: (id: string) => void;
  teams?: Array<{ id: string; name: string }>;
  activeTeamId?: string;
  onSwitchTeam?: (id: string) => void;
  showRequests?: boolean;
  showAdmin?: boolean;
  canManageMembers?: boolean;
  eaClubId?: string | null;
  onOpenEaMatch?: (match: EaMatch) => void;
}) {
  const { t } = useTranslation("shell");
  const navigate = useNavigate();
  const playersQuery = useQuery({
    queryKey: ["players", teamId, "palette"],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("players")
        .select("id, display_name, role_code, ea_sports_id, is_guest")
        .eq("team_id", teamId)
        .order("display_name");
      if (error) throw error;
      return (data || []) as Array<{
        id: string;
        display_name: string;
        role_code: string | null;
        ea_sports_id: string | null;
        is_guest: boolean;
      }>;
    },
    enabled: open,
    staleTime: 60_000,
  });
  const matchesQuery = useQuery({
    queryKey: ["ea-matches-palette", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("ea_match_snapshots")
        .select("ea_match_id, played_at, data, fetched_at")
        .eq("team_id", teamId)
        .order("played_at", { ascending: false, nullsFirst: false })
        .limit(50);
      if (error) throw error;
      return (data || []) as EaMatch[];
    },
    enabled: open && !!eaClubId && !!onOpenEaMatch,
    staleTime: 60_000,
  });
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setActiveIdx(0);
    }
  }, [open]);

  type Item =
    | { kind: "view"; key: string; label: string; hint: string; view: View }
    | { kind: "settings"; key: string; label: string; hint: string; hash: string }
    | { kind: "player"; key: string; label: string; hint: string; playerId: string }
    | { kind: "team"; key: string; label: string; hint: string; teamId: string }
    | { kind: "match"; key: string; label: string; hint: string; match: EaMatch };

  const items = useMemo<Item[]>(() => {
    const views: Item[] = [
      { kind: "view", key: "home", label: t("nav.home"), hint: t("commandPalette.viewHints.home"), view: "home" },
      { kind: "view", key: "players", label: t("nav.players"), hint: t("commandPalette.viewHints.players"), view: "players" },
      { kind: "view", key: "compare-players", label: t("nav.rosterSections.compare"), hint: t("commandPalette.viewHints.comparePlayers"), view: "compare-players" },
      { kind: "view", key: "top11", label: t("nav.rosterSections.top11"), hint: t("commandPalette.viewHints.top11"), view: "top11" },
      { kind: "view", key: "archetypes", label: t("nav.rosterSections.archetypes"), hint: t("commandPalette.viewHints.archetypes"), view: "archetypes" },
      { kind: "view", key: "guests", label: t("nav.guests"), hint: t("commandPalette.viewHints.guests"), view: "guests" },
      { kind: "view", key: "sessions", label: t("nav.sessions"), hint: t("commandPalette.viewHints.sessions"), view: "sessions" },
      { kind: "view", key: "stats", label: t("nav.stats"), hint: t("commandPalette.viewHints.stats"), view: "stats" },
      { kind: "view", key: "seasons", label: t("nav.seasons"), hint: t("commandPalette.viewHints.seasons"), view: "seasons" },
      { kind: "view", key: "ea", label: t("nav.ea"), hint: t("commandPalette.viewHints.ea"), view: "ea" },
      ...(showRequests
        ? ([{ kind: "view", key: "requests", label: t("nav.requests"), hint: t("commandPalette.viewHints.requests"), view: "requests" }] as Item[])
        : []),
      { kind: "view", key: "settings", label: t("nav.settings"), hint: t("commandPalette.viewHints.settings"), view: "settings" },
      ...(showAdmin
        ? ([{ kind: "view", key: "admin", label: t("nav.admin"), hint: t("commandPalette.viewHints.admin"), view: "admin" }] as Item[])
        : []),
    ];
    const settingsItems: Item[] = settingsSections
      .filter((s) => !s.adminOnly || canManageMembers)
      .map((s) => ({
        kind: "settings",
        key: `settings-${s.id}`,
        label: t(s.labelKey),
        hint: t("commandPalette.settingsSectionHint", { defaultValue: "Impostazioni" }),
        hash: s.hash,
      }));
    const players: Item[] = (playersQuery.data || []).map((p) => ({
      kind: "player",
      key: `player-${p.id}`,
      label: p.display_name,
      hint: [p.role_code || "—", p.ea_sports_id || "EA n/a", p.is_guest ? t("commandPalette.guestTag", { defaultValue: "guest" }) : t("commandPalette.rosterTag", { defaultValue: "roster" })].join(" · "),
      playerId: p.id,
    }));
    const teamItems: Item[] = (teams || [])
      .filter((tm) => tm.id !== activeTeamId)
      .map((tm) => ({
        kind: "team",
        key: `team-${tm.id}`,
        label: tm.name,
        hint: t("commandPalette.teamHint", { defaultValue: "switch team" }),
        teamId: tm.id,
      }));
    const matchItems: Item[] = eaClubId
      ? (matchesQuery.data || []).map((m) => {
          const d: any = m.data || {};
          const ourId = ourClubIdOf(d, eaClubId);
          const clubs: any = d.clubs || {};
          const ourClub: any = clubs[ourId] || {};
          const oppId = Object.keys(clubs).find((id) => id !== ourId);
          const oppClub: any = oppId ? clubs[oppId] : null;
          const oppName = fixEaText(oppClub?.details?.name) || (oppId || "—");
          const ourGoals = ourClub?.goals ?? "—";
          const oppGoals = oppClub?.goals ?? "—";
          const matchType = String(d.match_type || "");
          const typeShort = matchType
            ? (t(`matchTypes.${matchType}`, { ns: "mondofc", defaultValue: matchType }) as string)
            : "";
          const dateLabel = m.played_at
            ? new Date(m.played_at).toLocaleDateString(currentIntlLocale(), {
                day: "2-digit",
                month: "2-digit",
                year: "2-digit",
              })
            : "";
          return {
            kind: "match" as const,
            key: `match-${m.ea_match_id}`,
            label: `vs ${oppName} · ${ourGoals}-${oppGoals}`,
            hint: [typeShort, dateLabel].filter(Boolean).join(" · "),
            match: m,
          };
        })
      : [];
    const needle = query.trim().toLowerCase();
    // UX: palette aperta = pulita. Risultati solo dopo che l'utente digita.
    if (!needle) return [];
    const all = [...views, ...settingsItems, ...players, ...matchItems, ...teamItems];
    return all.filter((it) => `${it.label} ${it.hint}`.toLowerCase().includes(needle));
  }, [playersQuery.data, matchesQuery.data, query, t, teams, activeTeamId, eaClubId, showRequests, showAdmin, canManageMembers]);

  useEffect(() => {
    setActiveIdx((idx) => Math.min(idx, Math.max(0, items.length - 1)));
  }, [items.length]);

  const select = (item: Item) => {
    if (item.kind === "view") onChangeView(item.view);
    else if (item.kind === "settings") navigate(`/settings#${item.hash}`);
    else if (item.kind === "player") onOpenPlayer(item.playerId);
    else if (item.kind === "team" && onSwitchTeam) onSwitchTeam(item.teamId);
    else if (item.kind === "match" && onOpenEaMatch) onOpenEaMatch(item.match);
    onOpenChange(false);
  };

  // Chiudi su ESC (sostituisce il close-on-escape di Dialog shadcn).
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  // Focus dopo il mount per evitare race condition con createPortal +
  // l animazione del wrapper (autoFocus su input direttamente non scrolla
  // in modo affidabile su iOS).
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(id);
  }, [open]);

  if (!open || typeof window === "undefined") return null;

  /*
    Palette custom (no Dialog shadcn) per avere:
     - backdrop morbido (bg-black/15 light, /35 dark) invece di bg-black/80
     - apertura slide-from-bottom su mobile, fade su desktop
     - input text-base (16px) per evitare il famoso auto-zoom Safari iOS
       quando font-size < 16px (causava il layout shift segnalato)
     - mobile: ancorata sopra la bottom nav; desktop: centrata
     - niente X built-in, niente layout shift dovuto a Dialog primitive
  */
  return createPortal(
    <div
      className="fixed inset-0 z-50 animate-in fade-in-0 duration-150"
      role="dialog"
      aria-modal="true"
      aria-label={t("commandPalette.title", { defaultValue: "Quick navigation" })}
    >
      <button
        type="button"
        aria-label={t("common.close", { ns: "common", defaultValue: "Close" })}
        onClick={() => onOpenChange(false)}
        className="absolute inset-0 bg-black/15 dark:bg-black/40 backdrop-blur-[2px] cursor-default"
      />
      <div
        className={cn(
          "absolute pointer-events-auto bg-card border border-border rounded-2xl shadow-2xl overflow-hidden flex flex-col",
          "left-3 right-3 bottom-[calc(env(safe-area-inset-bottom)+5.5rem)]",
          "sm:left-1/2 sm:right-auto sm:bottom-auto sm:top-[20vh] sm:-translate-x-1/2 sm:w-full sm:max-w-xl",
          "animate-in fade-in-0 slide-in-from-bottom-2 sm:slide-in-from-top-2 duration-200"
        )}
      >
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIdx(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActiveIdx((i) => Math.min(items.length - 1, i + 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActiveIdx((i) => Math.max(0, i - 1));
              } else if (e.key === "Enter") {
                e.preventDefault();
                if (items[activeIdx]) select(items[activeIdx]);
              }
            }}
            placeholder={t("commandPalette.placeholder")}
            inputMode="search"
            enterKeyHint="search"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            // text-base (16px) e font-size esplicito anche via inline-style:
            // doppia cintura per evitare il bug del Safari iOS che zooma sugli
            // input < 16px (uno dei due tutela anche in caso di override).
            style={{ fontSize: 16 }}
            className="flex-1 bg-transparent outline-none border-0 text-base h-10 placeholder:text-muted-foreground"
          />
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label={t("common.close", { ns: "common", defaultValue: "Close" })}
            className="h-8 w-8 shrink-0 rounded-full grid place-items-center text-muted-foreground hover:bg-muted/60 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[50vh] sm:max-h-[60vh] overflow-y-auto py-2">
          {!query.trim() ? (
            <div className="px-4 py-6 text-xs text-muted-foreground space-y-1.5">
              <p className="font-medium text-foreground/80">{t("commandPalette.emptyTitle", { defaultValue: "Inizia a scrivere per cercare" })}</p>
              <p>{t("commandPalette.emptyHint")}</p>
            </div>
          ) : items.length === 0 ? (
            <p className="text-center text-xs text-muted-foreground py-6">{t("commandPalette.noResults")}</p>
          ) : null}
          {(() => {
            let lastKind: string | null = null;
            return items.map((item, idx) => {
              const showHeader = item.kind !== lastKind;
              lastKind = item.kind;
              return (
                <Fragment key={item.key}>
                  {showHeader && (
                    <p className="eb-eyebrow px-3 pt-2 pb-1">
                      {item.kind === "view"
                        ? t("commandPalette.sections.views")
                        : item.kind === "settings"
                          ? t("commandPalette.sections.settings", { defaultValue: "Impostazioni" })
                          : item.kind === "team"
                            ? t("commandPalette.sections.teams", { defaultValue: "Teams" })
                            : item.kind === "match"
                              ? t("commandPalette.sections.matches", { defaultValue: "Matches" })
                              : t("commandPalette.sections.players")}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => select(item)}
                    onMouseEnter={() => setActiveIdx(idx)}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition-colors",
                      idx === activeIdx ? "bg-muted/70" : "hover:bg-muted/40"
                    )}
                  >
                    <span
                      className={cn(
                        "h-1.5 w-1.5 rounded-full",
                        item.kind === "view"
                          ? "bg-primary"
                          : item.kind === "settings"
                            ? "bg-violet-500"
                            : item.kind === "match"
                              ? "bg-emerald-500"
                              : item.kind === "team"
                                ? "bg-sky-500"
                                : "bg-muted-foreground"
                      )}
                    />
                    <span className="font-medium flex-1 truncate">{item.label}</span>
                    <span className="text-[11px] text-muted-foreground truncate">
                      {item.hint}
                      {item.kind === "view" && currentView === item.view ? " · " + t("commandPalette.active", { defaultValue: "active" }) : ""}
                    </span>
                  </button>
                </Fragment>
              );
            });
          })()}
        </div>
        {/* Hint tastiera: nascosto su mobile (no tastiera fisica). */}
        <div className="hidden sm:flex border-t border-border bg-muted/30 px-3 py-1.5 items-center gap-2 text-[11px] text-muted-foreground">
          <span className="eb-kbd">↑</span>
          <span className="eb-kbd">↓</span>
          <span>{t("commandPalette.navigate", { defaultValue: "navigate" })}</span>
          <span className="eb-kbd ml-auto">↵</span>
          <span>{t("commandPalette.select", { defaultValue: "select" })}</span>
        </div>
      </div>
    </div>,
    document.body
  );
}
